import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Cpu, ArrowRight, CheckCircle2, Zap, Globe,
  Workflow, Bot, Layout, BadgeDollarSign, Sparkles, Users
} from 'lucide-react';

const products = [
  {
    id: 'flow',
    name: 'Moxey Flow',
    tagline: 'CRM, Transactions, Commissions & E-Sign',
    description: 'Manage your entire pipeline from lead to close. A modern CRM with transaction management, commission tracking, reporting, direct deposit, and built-in e-signatures.',
    icon: Workflow,
    gradient: 'from-violet-600 to-purple-500',
    accent: '#8B5CF6',
    features: [
      'Contact & lead management',
      'Transaction pipeline tracking',
      'Commission tracking & reporting',
      'Direct deposit payouts',
      'Built-in electronic signatures',
      'Automated follow-ups & reminders',
      'Document storage & management'
    ]
  },
  {
    id: 'ai',
    name: 'Moxey AI',
    tagline: 'AI Employees for Real Estate',
    description: 'Put AI to work. Draft messages, analyze markets, generate listings, and automate repetitive tasks.',
    icon: Bot,
    gradient: 'from-blue-600 to-blue-500',
    accent: '#3B82F6',
    features: [
      'AI-powered message drafting',
      'Market analysis & insights',
      'Listing description generator',
      'Smart task automation',
      'Contextual recommendations'
    ]
  }
];

export default function MoxeyOS() {
  return (
    <div className="space-y-6" data-testid="moxey-os-page">

      {/* Hero */}
      <div className="rounded-[18px] overflow-hidden bg-[#1E293B] relative p-8 lg:p-10">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-br from-indigo-500/10 to-violet-500/10 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" />
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-[12px] bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <Cpu className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">Moxey OS</h1>
              <p className="text-slate-400 text-[13px]">The operating system for your real estate business</p>
            </div>
          </div>
          <p className="text-[15px] text-slate-300 leading-relaxed max-w-xl">
            An integrated suite of tools — CRM, AI assistants, websites, and commission management — designed exclusively for AREA members.
          </p>
          <div className="flex flex-wrap gap-5 mt-6">
            {[
              { icon: Sparkles, label: 'AI-Powered', color: '#FBBF24' },
              { icon: Zap, label: 'Built for AREA', color: '#34D399' },
              { icon: Users, label: 'Directory Integrated', color: '#60A5FA' },
            ].map((tag, i) => (
              <div key={i} className="flex items-center gap-2 text-[12px] text-slate-400 font-medium">
                <tag.icon className="w-4 h-4" style={{ color: tag.color }} />
                {tag.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {products.map((p) => (
          <Card key={p.id}
            className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] bg-white overflow-hidden group"
            data-testid={`moxey-${p.id}`}
          >
            <CardContent className="p-0">
              {/* Card Header */}
              <div className={`bg-gradient-to-r ${p.gradient} px-6 py-4 flex items-center gap-3`}>
                <div className="w-10 h-10 rounded-[10px] bg-white/20 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{p.name}</h3>
                  <p className="text-white/75 text-[12px]">{p.tagline}</p>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6">
                <p className="text-[13px] text-slate-600 leading-relaxed mb-5">{p.description}</p>
                <ul className="space-y-2.5 mb-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-[13px] text-slate-700">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: p.accent }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full h-10 rounded-xl bg-slate-900 hover:bg-slate-800 text-[13px] font-medium"
                  data-testid={`moxey-${p.id}-cta`}>
                  Explore {p.name}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bottom CTA */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.04)] rounded-[18px] bg-[#1E293B] overflow-hidden">
        <CardContent className="p-8 lg:p-10 text-center relative">
          <div className="absolute top-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 -translate-x-1/2 blur-3xl" />
          <div className="relative z-10">
            <h2 className="text-xl lg:text-2xl font-bold text-white mb-2">Ready to power your business?</h2>
            <p className="text-slate-400 text-[14px] max-w-md mx-auto mb-6">
              Moxey OS is available at discounted pricing for all AREA members.
            </p>
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl px-8 h-11 text-[13px] font-semibold">
              Get Started with Moxey OS
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
